import { type AccessState, isLocked, parseAccessState } from '@lingoping/core';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { StorageManager } from '../storage/index.js';

/**
 * The learner's access (trial, subscription, locked) as the server last said it
 * (Docs/plans/SUBSCRIPTION_PLAN.md). The sync engine saves it from every sync answer and from a
 * `402 access_locked` refusal; screens read it here and redraw when it changes. The server
 * decides; this is only the device's note of the answer, kept per account so a sign-in as
 * someone else never shows the previous learner's state.
 */

const ACCESS_KEY = 'lingoping_access';

interface SavedAccess {
  userId: string;
  access: AccessState;
  at: string;
}

const listeners = new Set<(access: AccessState | null) => void>();

/** The last known access of the signed-in learner, or null (unknown, or signed out). */
export async function getAccess(): Promise<AccessState | null> {
  const saved = await StorageManager.getLocalValue<SavedAccess>(ACCESS_KEY);
  if (!saved) return null;
  const userId = await StorageManager.getCurrentUserId();
  return userId && saved.userId === userId ? parseAccessState(saved.access) : null;
}

/** Saves what the server said (the raw `access` object); malformed answers are ignored. */
export async function saveAccess(userId: string, raw: unknown): Promise<AccessState | null> {
  const access = parseAccessState(raw);
  if (!access) return null;
  const saved = await StorageManager.getLocalValue<SavedAccess>(ACCESS_KEY);
  if (saved?.userId === userId && JSON.stringify(saved.access) === JSON.stringify(access)) {
    return access;
  }
  await StorageManager.setLocalValue<SavedAccess>(ACCESS_KEY, {
    userId,
    access,
    at: new Date().toISOString(),
  });
  for (const listener of listeners) listener(access);
  return access;
}

/** On sign-out. */
export async function clearAccess(): Promise<void> {
  await StorageManager.setLocalValue(ACCESS_KEY, null);
  for (const listener of listeners) listener(null);
}

/**
 * Calls `listener` whenever the saved access changes: in this page directly, and in the
 * extension also when another page (the background worker, the popup) saved it.
 * Returns an unsubscribe function.
 */
export function onAccessChange(listener: (access: AccessState | null) => void): () => void {
  listeners.add(listener);
  const storage = typeof chrome !== 'undefined' ? chrome.storage : undefined;
  const onChanged = (changes: Record<string, unknown>, area: string) => {
    if (area === 'local' && ACCESS_KEY in changes) void getAccess().then(listener);
  };
  storage?.onChanged?.addListener(onChanged);
  return () => {
    listeners.delete(listener);
    storage?.onChanged?.removeListener(onChanged);
  };
}

/**
 * An AI function (`generate-card`, `card-image`, `normalize-words`, `translate-words`,
 * `word-senses`) refused with `402 access_locked`: the learner has no trial, subscription or
 * grant. The access it carried is saved already, so screens can show the lock from `getAccess`
 * or `onAccessChange`; `access` is the same, for the one that caught this.
 */
export class AccessLockedError extends Error {
  constructor(public readonly access: AccessState | null) {
    super('Your access to LingoPing has ended.');
    this.name = 'AccessLockedError';
  }
}

/**
 * Call with the error of a failed `functions.invoke`: when it is the `402 access_locked` refusal,
 * saves the access from the body and throws `AccessLockedError`; any other error is left alone.
 */
export async function throwIfAccessLocked(error: unknown): Promise<void> {
  const locked = await noteAccessLocked(error);
  if (locked) throw new AccessLockedError(locked.access);
}

/**
 * The same without throwing, for background work that has nobody to tell: when `error` is the
 * `402 access_locked` refusal, saves the access from its body and answers it (`access` is null if
 * the body held none); null for any other error.
 */
export async function noteAccessLocked(
  error: unknown
): Promise<{ access: AccessState | null } | null> {
  if (!(error instanceof FunctionsHttpError) || error.context?.status !== 402) return null;
  let body: { error?: unknown; access?: unknown } | null;
  try {
    body = await (error.context as Response).clone().json();
  } catch {
    return null;
  }
  if (body?.error !== 'access_locked') return null;
  const userId = await StorageManager.getCurrentUserId();
  return { access: userId ? await saveAccess(userId, body.access) : null };
}

/**
 * Whether the saved access says the learner is locked out. Background work (pending cards,
 * pictures, translations) checks it first and does nothing while it is true, instead of asking
 * the server again and again; the sync's pulls note when access is back.
 */
export async function isAccessLocked(): Promise<boolean> {
  return isLocked(await getAccess());
}
